/*
 * EGR on Shopify — data layer. Runs after egr-data.js and before egr-core.js.
 *
 * Shopify is the source of truth for what is for sale: which products exist,
 * their strengths (variants), prices, availability and photos all come from the
 * store (window.EGR_SHOPIFY_PRODUCTS, written by snippets/egr-config.liquid), so
 * edits in the Shopify admin show up on the site. egr-data.js only adds the
 * research details Shopify has no fields for (category, label tone, sequence,
 * molecular weight, aliases).
 *
 * Also applied here:
 * - Store details (email, phone, address, payment badges, free-shipping threshold)
 *   come from the theme settings (Customize > Theme settings > Store details).
 * - The static site's client-side offers (daily half-price list, bulk tiers,
 *   multi-buy, monthly box, sitewide sale) are switched off: Shopify's checkout
 *   would not apply them, so showing them would mislead buyers. Recreate any you
 *   want as Shopify discounts first.
 * - Lot numbers and certificates come only from window.EGR_LOTS (assets/egr-lots.js),
 *   keyed by variant SKU. It ships empty: no sample lots appear on the live store.
 */
(function () {
  "use strict";
  const D = window.EGR_DATA;
  const S = window.EGR_SHOP || {};
  const st = S.settings || {};

  // ---- store details from theme settings
  Object.assign(D.brand, {
    email: st.email || "",
    phone: st.phone || "",
    address: st.address || "",
    legalName: st.legalName || D.brand.legalName,
    replyTime: st.replyTime || D.brand.replyTime,
    paymentMethods: String(st.paymentBadges || "").split(",").map((s) => s.trim()).filter(Boolean)
  });
  // No badges typed in: show the methods actually enabled in Settings > Payments.
  if (!D.brand.paymentMethods.length && Array.isArray(st.paymentTypes)) {
    const NAMES = { visa: "Visa", master: "Mastercard", american_express: "Amex", discover: "Discover", diners_club: "Diners Club", jcb: "JCB", maestro: "Maestro", paypal: "PayPal", apple_pay: "Apple Pay", google_pay: "Google Pay", shopify_pay: "Shop Pay", amazon: "Amazon Pay", venmo: "Venmo", klarna: "Klarna", afterpay: "Afterpay", bitcoin: "Bitcoin" };
    D.brand.paymentMethods = st.paymentTypes.map((t) => NAMES[t] || "").filter(Boolean);
  }

  // ---- promotions: off (see header)
  const P = D.promo;
  P.sitewide.enabled = false;
  P.daily.enabled = false;
  P.daily.handles = [];
  P.bulk.tiers = [];
  P.multi.tiers = [];
  P.box.enabled = false;
  P.freeShippingOver = Number(st.freeShippingOver) > 0 ? Number(st.freeShippingOver) : 0;

  // ---- lots: only real ones, keyed by SKU
  const LOTS = window.EGR_LOTS || {};
  const lotsFor = (sku) => (LOTS[sku] || []).map((l) => Object.assign({
    status: "active", method: "RP-HPLC · 220 nm", purity: null, massTheoretical: null, massObserved: null,
    identity: true, lab: null, coaUrl: null
  }, l));

  // ---- merge with the Shopify catalog
  const shop = Array.isArray(window.EGR_SHOPIFY_PRODUCTS) ? window.EGR_SHOPIFY_PRODUCTS : null;
  if (!shop) return; // no Shopify data (should not happen on the store); keep egr-data.js as is

  const key = (s) => String(s || "").toLowerCase().replace(/\s+/g, "");
  const optionTitle = (sv) => (sv.option1 && sv.option1 !== "Default Title" ? sv.option1 : sv.title === "Default Title" ? "" : sv.title);
  const mgOf = (t) => { const m = /([\d.]+)\s*mg/i.exec(t || ""); return m ? parseFloat(m[1]) : null; };
  const mlOf = (t) => { const m = /([\d.]+)\s*ml/i.exec(t || ""); return m ? parseFloat(m[1]) : null; };
  const catIds = D.categories.map((c) => c.id);

  // The product photos that ship with the theme (snippets/egr-photos.liquid), used
  // until the product has its own photos in Shopify. [card size, large].
  const PHOTOS = window.EGR_THEME_PHOTOS || {};
  const photoKey = (handle, title) => handle + "|" + String(title || "").replace(/s+/g, "").toLowerCase();

  function variantFrom(sv, base, handle) {
    const v = Object.assign({}, base || {});
    const title = optionTitle(sv) || (base && base.title) || "Standard";
    v.id = String(sv.id);
    v.title = title;
    v.sku = sv.sku || v.sku || "";
    v.price = sv.price / 100;
    v.compareAt = sv.compare ? sv.compare / 100 : null;
    v.available = !!sv.available;
    v.stock = sv.available ? null : 0; // cards show "Sold out" when 0
    v.image = sv.image || null;
    v.photo = PHOTOS[photoKey(handle, title)] || null;
    if (v.mg == null) v.mg = mgOf(title);
    if (v.volumeMl == null && mlOf(title)) v.volumeMl = mlOf(title);
    v.lots = lotsFor(v.sku);
    return v;
  }

  const byHandle = new Map(shop.map((sp) => [sp.handle, sp]));
  const merged = [];
  D.products.forEach((p) => {
    const sp = byHandle.get(p.handle);
    if (!sp) return; // not in the store (yet)
    byHandle.delete(p.handle);
    const used = new Set();
    p.variants = sp.variants.map((sv) => {
      const base = p.variants.find((x) => !used.has(x) && x.sku && sv.sku && x.sku === sv.sku) ||
        p.variants.find((x) => !used.has(x) && key(x.title) === key(optionTitle(sv)));
      if (base) used.add(base);
      return variantFrom(sv, base, p.handle);
    });
    p.title = sp.title;
    p.shopifyId = sp.id;
    p.available = sp.available;
    p.image = sp.image || null;
    merged.push(p);
  });

  // Products added in Shopify that egr-data.js doesn't describe yet: show them with
  // sensible defaults (category from a matching tag, otherwise "Other").
  byHandle.forEach((sp) => {
    const tags = (sp.tags || []).map((t) => String(t).toLowerCase());
    merged.push({
      handle: sp.handle, title: sp.title, short: sp.title,
      subtitle: sp.type || "", category: catIds.find((c) => tags.indexOf(c) >= 0) || "other",
      tone: "sky", kind: tags.indexOf("water") >= 0 ? "water" : "vial",
      spec: { sequence: null, length: null, mw: null, formula: null, cas: null, pubchemCid: null, form: "", storageC: null, purityMin: null },
      variants: sp.variants.map((sv) => variantFrom(sv, null, sp.handle)),
      badges: tags.indexOf("bestseller") >= 0 ? ["bestseller"] : [],
      aliases: [], featuredRank: 0,
      shopifyId: sp.id, available: sp.available, image: sp.image || null
    });
  });

  D.products = merged.filter((p) => p.variants.length);
  if (D.products.some((p) => p.category === "other")) D.categories.push({ id: "other", label: "Other" });
})();
